import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuit, Check, MessageCircle, User, X, Send, Phone, Mail, MapPin } from 'lucide-react';
const matches = [{
  id: 1,
  name: 'David O.',
  age: 21,
  dept: 'Computer Science',
  match: 95,
  tags: ['Night Owl', 'Gamer', 'Quiet'],
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Final year CS student. Love gaming and coding late at night. Looking for someone who respects quiet study time.',
  location: 'Unilag, Akoka',
  phone: '+234 803 456 7890',
  email: 'david@student.edu.ng'
}, {
  id: 2,
  name: 'Sarah K.',
  age: 20,
  dept: 'Mass Comm',
  match: 88,
  tags: ['Social', 'Tidy', 'Cooks'],
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Love cooking and keeping things organized. Social but also respect personal space. Looking for a clean roommate!',
  location: 'Yaba Tech, Yaba',
  phone: '+234 805 123 4567',
  email: 'sarah@student.edu.ng'
}, {
  id: 3,
  name: 'Emmanuel J.',
  age: 22,
  dept: 'Engineering',
  match: 82,
  tags: ['Studious', 'Early Bird', 'Gym'],
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Engineering student who loves early morning workouts. Serious about studies but also know how to have fun on weekends.',
  location: 'LUTH, Idi-Araba',
  phone: '+234 807 890 1234',
  email: 'emmanuel@student.edu.ng'
}];
export function RoommateMatchPage() {
  const [step, setStep] = useState<'intro' | 'quiz' | 'results'>('intro');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedMatch, setSelectedMatch] = useState<(typeof matches)[0] | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{
    id: number;
    text: string;
    sender: 'me' | 'them';
    time: string;
  }>>([{
    id: 1,
    text: "Hi! I saw we're a great match. Would love to chat about potentially being roommates!",
    sender: 'me',
    time: '10:30 AM'
  }, {
    id: 2,
    text: "Hey! Yes, I'd love to discuss that. What hostel are you looking at?",
    sender: 'them',
    time: '10:32 AM'
  }]);
  const [newMessage, setNewMessage] = useState('');
  const questions = [{
    id: 'sleep',
    label: "What's your typical sleep schedule?",
    options: ['Early Bird (10pm)', 'Night Owl (2am)', 'Flexible']
  }, {
    id: 'clean',
    label: 'How clean are you?',
    options: ['Spick & Span', 'Reasonably Tidy', 'Organized Chaos']
  }, {
    id: 'guests',
    label: 'How do you feel about guests?',
    options: ['Love them', 'Occasional is fine', 'Prefer privacy']
  }, {
    id: 'budget',
    label: 'Monthly budget for rent?',
    options: ['< ₦50k', '₦50k - ₦100k', '> ₦100k']
  }];
  const sendMessage = () => {
    if (newMessage.trim()) {
      const newMsg = {
        id: messages.length + 1,
        text: newMessage,
        sender: 'me' as const,
        time: new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setMessages([...messages, newMsg]);
      setNewMessage('');
      setTimeout(() => {
        const response = {
          id: messages.length + 2,
          text: "That sounds great! Let me know when you'd like to meet up.",
          sender: 'them' as const,
          time: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })
        };
        setMessages(prev => [...prev, response]);
      }, 2000);
    }
  };
  return <div className="min-h-screen bg-slate-50 pt-20" data-id="element-930">
      <Navigation data-id="element-931" />

      {/* Chat Modal */}
      <AnimatePresence data-id="element-932">
        {chatOpen && selectedMatch && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setChatOpen(false)} data-id="element-933">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-2xl w-full h-[600px] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-934">
              <div className="flex items-center justify-between p-4 border-b border-slate-200" data-id="element-935">
                <div className="flex items-center gap-3" data-id="element-936">
                  <img src={selectedMatch.image} alt={selectedMatch.name} className="w-10 h-10 rounded-full object-cover" data-id="element-937" />
                  <div data-id="element-938">
                    <h3 className="font-bold text-brand-dark" data-id="element-939">
                      {selectedMatch.name}
                    </h3>
                    <p className="text-xs text-emerald-600" data-id="element-940">● Online</p>
                  </div>
                </div>
                <button onClick={() => setChatOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-941">
                  <X className="w-5 h-5" data-id="element-942" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-943">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`} data-id="element-944">
                    <div className={`max-w-[70%] ${msg.sender === 'me' ? 'order-2' : 'order-1'}`} data-id="element-945">
                      <div className={`rounded-2xl px-4 py-2 ${msg.sender === 'me' ? 'bg-brand-primary text-white' : 'bg-slate-100 text-slate-900'}`} data-id="element-946">
                        <p className="text-sm" data-id="element-947">{msg.text}</p>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 px-2" data-id="element-948">
                        {msg.time}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="p-4 border-t border-slate-200" data-id="element-949">
                <div className="flex gap-2" data-id="element-950">
                  <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendMessage()} placeholder="Type a message..." className="flex-1 px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-brand-primary focus:outline-none" data-id="element-951" />
                  <button onClick={sendMessage} className="w-10 h-10 bg-brand-primary hover:bg-brand-light text-white rounded-full flex items-center justify-center transition-colors" data-id="element-952">
                    <Send className="w-5 h-5" data-id="element-953" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Profile Detail Modal */}
      <AnimatePresence data-id="element-954">
        {selectedMatch && !chatOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSelectedMatch(null)} data-id="element-955">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-956">
              <div className="relative" data-id="element-957">
                <img src={selectedMatch.image} alt={selectedMatch.name} className="w-full h-64 object-cover rounded-t-2xl" data-id="element-958" />
                <button onClick={() => setSelectedMatch(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors" data-id="element-959">
                  <X className="w-5 h-5" data-id="element-960" />
                </button>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-961">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4" data-id="element-962">
                    <h3 className="text-2xl font-bold text-brand-dark" data-id="element-963">
                      {selectedMatch.name}
                    </h3>
                    <p className="text-slate-600" data-id="element-964">
                      {selectedMatch.age} • {selectedMatch.dept}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6" data-id="element-965">
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-966">
                  <div className="text-4xl font-bold text-emerald-600" data-id="element-967">
                    {selectedMatch.match}%
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-968">Match Score</div>
                </div>

                <div className="flex flex-wrap gap-2 justify-center mb-6" data-id="element-969">
                  {selectedMatch.tags.map(tag => <Badge key={tag} variant="neutral" data-id="element-970">
                      {tag}
                    </Badge>)}
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-971">
                  <h4 className="font-bold text-brand-dark mb-2" data-id="element-972">About</h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-973">
                    {selectedMatch.bio}
                  </p>
                </div>

                <div className="space-y-3 mb-6" data-id="element-974">
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-975">
                    <MapPin className="w-4 h-4" data-id="element-976" />
                    <span className="text-sm" data-id="element-977">{selectedMatch.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-978">
                    <Phone className="w-4 h-4" data-id="element-979" />
                    <span className="text-sm" data-id="element-980">{selectedMatch.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-981">
                    <Mail className="w-4 h-4" data-id="element-982" />
                    <span className="text-sm" data-id="element-983">{selectedMatch.email}</span>
                  </div>
                </div>

                <Button className="w-full" onClick={() => {
              setChatOpen(true);
            }} data-id="element-984">
                  <MessageCircle className="w-4 h-4 mr-2" data-id="element-985" />
                  Start Chat
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <Section data-id="element-986">
        <div className="max-w-6xl mx-auto" data-id="element-987">
          <AnimatePresence mode="wait" data-id="element-988">
            {step === 'intro' && <motion.div key="intro" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-989">
                {/* Left: Text Content */}
                <div className="text-center lg:text-left" data-id="element-990">
                  <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-6" data-id="element-991">
                    <BrainCircuit className="w-10 h-10" data-id="element-992" />
                  </div>
                  <h1 className="text-4xl font-bold text-brand-dark mb-4" data-id="element-993">
                    Find Your Vibe Tribe
                  </h1>
                  <p className="text-xl text-slate-600 mb-8" data-id="element-994">
                    Stop living with strangers who clash with your lifestyle.
                    Take our 2-minute "Vibe Check" to find compatible roommates.
                  </p>
                  <Button size="lg" onClick={() => setStep('quiz')} data-id="element-995">
                    Start Vibe Check
                  </Button>
                </div>

                {/* Right: Illustration */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.2,
              duration: 0.6
            }} className="relative" data-id="element-996">
                  <img src="https://cdn.magicpatterns.com/uploads/42YNqY8a5G5zatBrPUmek9/Copy_of_Billvast_Shirt_Mock-up_(2).png" alt="AI Roommate Matching" className="w-full h-auto max-w-md mx-auto drop-shadow-2xl" data-id="element-997" />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-xl" data-id="element-998">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-999">
                      AI-Powered Matching
                    </div>
                    <div className="text-2xl font-bold text-brand-primary" data-id="element-1000">
                      95% Success Rate
                    </div>
                  </div>
                </motion.div>
              </motion.div>}

            {step === 'quiz' && <motion.div key="quiz" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-1001">
                <Card data-id="element-1002">
                  <CardContent className="p-8" data-id="element-1003">
                    <h2 className="text-2xl font-bold mb-6" data-id="element-1004">
                      Tell us about yourself
                    </h2>
                    <div className="space-y-8" data-id="element-1005">
                      {questions.map(q => <div key={q.id} data-id="element-1006">
                          <label className="block text-lg font-medium text-brand-dark mb-3" data-id="element-1007">
                            {q.label}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1008">
                            {q.options.map(opt => <button key={opt} onClick={() => setAnswers({
                        ...answers,
                        [q.id]: opt
                      })} className={`p-3 rounded-lg border text-sm font-medium transition-all ${answers[q.id] === opt ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`} data-id="element-1009">
                                {opt}
                              </button>)}
                          </div>
                        </div>)}
                    </div>
                    <div className="mt-8 flex justify-end" data-id="element-1010">
                      <Button onClick={() => setStep('results')} disabled={Object.keys(answers).length < questions.length} data-id="element-1011">
                        Find Matches
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>}

            {step === 'results' && <motion.div key="results" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-1012">
                <div className="flex justify-between items-center mb-6" data-id="element-1013">
                  <h2 className="text-2xl font-bold text-brand-dark" data-id="element-1014">
                    We found {matches.length} potential matches!
                  </h2>
                  <Button variant="outline" size="sm" onClick={() => setStep('quiz')} data-id="element-1015">
                    Retake Quiz
                  </Button>
                </div>

                <div className="grid gap-4" data-id="element-1016">
                  {matches.map((match, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.1
              }} data-id="element-1017">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedMatch(match)} data-id="element-1018">
                        <CardContent className="flex flex-col sm:flex-row items-center gap-6 p-6" data-id="element-1019">
                          <img src={match.image} alt={match.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-slate-100" data-id="element-1020" />

                          <div className="flex-1 text-center sm:text-left" data-id="element-1021">
                            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1" data-id="element-1022">
                              <h3 className="text-xl font-bold text-brand-dark" data-id="element-1023">
                                {match.name}
                              </h3>
                              <span className="text-sm text-slate-500" data-id="element-1024">
                                ({match.age})
                              </span>
                            </div>
                            <p className="text-slate-600 mb-2" data-id="element-1025">{match.dept}</p>
                            <div className="flex flex-wrap justify-center sm:justify-start gap-2" data-id="element-1026">
                              {match.tags.map(tag => <Badge key={tag} variant="neutral" data-id="element-1027">
                                  {tag}
                                </Badge>)}
                            </div>
                          </div>

                          <div className="text-center" data-id="element-1028">
                            <div className="text-3xl font-bold text-emerald-600 mb-1" data-id="element-1029">
                              {match.match}%
                            </div>
                            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-3" data-id="element-1030">
                              Match Score
                            </div>
                            <Button size="sm" onClick={e => {
                        e.stopPropagation();
                        setSelectedMatch(match);
                        setChatOpen(true);
                      }} data-id="element-1031">
                              <MessageCircle className="w-4 h-4 mr-2" data-id="element-1032" />
                              Chat
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </Section>
    </div>;
}